import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { format } from 'date-fns';
import {
  Avatar,
  avatarType,
  btnSize,
  btnType,
  Button,
  colors,
  fontWeight,
  Heading,
  headingLevel,
  Link,
  linkType,
  Text,
  textLevel,
} from '../../../../../../packages';
import { ProfileInfo } from '../../../../../../packages/ui/templates/profile-info';
import { useStyles } from '../../../../../admin/claim-detail/ClaimDetail.styles';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { da } from 'date-fns/locale';

const defaultData = {
  dob: '',
  firstName: '',
  fullName: '',
  gender: '',
  lastName: '',
  phoneNumber: '',
  profileImage: '',
  ssn: '',
};

const hasFieldValue = object => {
  return Object.values(object).some(value => value !== '' && value !== null && value !== undefined);
};

const Payer = ({ data = defaultData, setOpenPayerDrawer = null }) => {
  const classes = useStyles();
  const {
    name = data?.name || '',
    code = data?.code || '',
    isCreditCardOptional = data?.isCreditCardOptional || false,
    // isActive = data?.isActive || false,
    takeDeposit = data?.takeDeposit || false,
    depositAmount = data?.takeDeposit ? data?.depositAmount : 0,
    candidPayerId = data?.candidPayerId ?? '',
    candidPayerName = data?.candidPayerName ?? '',
    image = data?.image || '',
    parProofNotes = data?.parProofNotes || '',
    parProofDate = data?.parProofDate || '',
    parProofDocument = data?.parProofDocument || '',

    credentialingPrimaryContact = {
      firstName: data?.credentialingPrimaryContact?.firstName || '',
      lastName: data?.credentialingPrimaryContact?.lastName || '',
      title: data?.credentialingPrimaryContact?.title || '',
      phone: data?.credentialingPrimaryContact?.phone || '',
      email: data?.credentialingPrimaryContact?.email || '',
      fax: data?.credentialingPrimaryContact?.fax || '',
      note: data?.credentialingPrimaryContact?.note || '',
    },
    credentialingSecondaryContact = {
      firstName: data?.credentialingSecondaryContact?.firstName || '',
      lastName: data?.credentialingSecondaryContact?.lastName || '',
      title: data?.credentialingSecondaryContact?.title || '',
      phone: data?.credentialingSecondaryContact?.phone || '',
      email: data?.credentialingSecondaryContact?.email || '',
      fax: data?.credentialingSecondaryContact?.fax || '',
      note: data?.credentialingSecondaryContact?.note || '',
    },
    billingPrimaryContact = {
      firstName: data?.billingPrimaryContact?.firstName || '',
      lastName: data?.billingPrimaryContact?.lastName || '',
      title: data?.billingPrimaryContact?.title || '',
      phone: data?.billingPrimaryContact?.phone || '',
      email: data?.billingPrimaryContact?.email || '',
      fax: data?.billingPrimaryContact?.fax || '',
      note: data?.billingPrimaryContact?.note || '',
    },
    billingSecondaryContact = {
      firstName: data?.billingSecondaryContact?.firstName || '',
      lastName: data?.billingSecondaryContact?.lastName || '',
      title: data?.billingSecondaryContact?.title || '',
      phone: data?.billingSecondaryContact?.phone || '',
      email: data?.billingSecondaryContact?.email || '',
      fax: data?.billingSecondaryContact?.fax || '',
      note: data?.billingSecondaryContact?.note || '',
    },
  } = data;
  const SectionTitle = ({ title = '' }) => (
    <Typography
      sx={{
        fontSize: '12px',
        fontWeight: 800,
        lineHeight: '20px',
        letterSpacing: '1px',
        width: '100%',
        textTransform: 'uppercase',
        color: colors.neutral900,
      }}
    >
      {title}
    </Typography>
  );

  const Col = ({ label, value, tel = false, email = false, link = false, img = false }) => (
    <Box display="flex" flexDirection="column" minWidth="160px">
      <Text className={classes.colLabel} level={textLevel.S} weight={fontWeight.SEMI_BOLD}>
        {label}
      </Text>
      {img && value ? (
        <div onClick={() => window.open(value, '_blank')}>
          <Avatar src={value} variant={avatarType.SQUARE} size={64} />
        </div>
      ) : tel || email || link ? (
        <Link
          variant={linkType.BLANK}
          href={value ? (tel ? `tel:${value}` : email ? `mailto:${value}` : value) : '#'}
          className={clsx([classes.marginTop8, classes.link])}
          level={textLevel.M}
          weight={fontWeight.MEDIUM}
        >
          {value || 'N/A'}
        </Link>
      ) : (
        <Text className={classes.marginTop8} level={textLevel.M} weight={fontWeight.MEDIUM}>
          {value || 'N/A'}
        </Text>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        background: colors.white,
        boxShadow:
          '0px 4px 8px rgba(0, 0, 0, 0.04), 0px 0px 2px rgba(0, 0, 0, 0.06), 0px 0px 1px rgba(0, 0, 0, 0.04)',
        borderRadius: 2,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" pl={3} py="12px" pr={2}>
        <Heading level={headingLevel.S} weight={fontWeight.SEMI_BOLD}>
          Payer Details
        </Heading>
        <Button
          className={classes.btn}
          variant={btnType.TEXT}
          onClick={() => {
            setOpenPayerDrawer && setOpenPayerDrawer(true);
          }}
          size={btnSize.SMALL}
        >
          Edit Payer
        </Button>
      </Box>
      <Divider />

      <Box display="flex" p={5} flexWrap="wrap" gap={3} rowGap={4}>
        {/* <Col label="Payer Name" value={name} /> */}
        <Col label="Payer Code" value={code} />
        <Col label="Candid Payer ID" value={candidPayerId} />
        <Col label="Candid Payer Name" value={candidPayerName} />
        <Col label="Is Credit Card Optional" value={isCreditCardOptional ? 'Yes' : 'No'} />
        <Col label="Take Deposit" value={takeDeposit ? 'Yes' : 'No'} />
        {takeDeposit && <Col label="Deposit Amount" value={`$${depositAmount}`} />}
        {image && <Col label="Logo Image" value={image} img />}
      </Box>
      <Box display="flex" px={4} pb={4} flexWrap="wrap" gap={3} rowGap={4}>
        <Col label="PAR Proof Notes" value={parProofNotes} />
        <Col
          label="PAR Proof Date"
          value={parProofDate ? format(new Date(parProofDate), 'MM/dd/yyyy') : 'N/A'}
        />
        <Col label="PAR Proof Document" value={parProofDocument} img />
      </Box>
      {hasFieldValue(credentialingPrimaryContact) && (
        <Box display="flex" flexDirection="column" px={4} pb={4} gap={2} rowGap={2}>
          <SectionTitle title="Credentialing Primary Contact" />
          <Box display="flex" flexWrap="wrap" gap={3} rowGap={4}>
            {credentialingPrimaryContact?.firstName && (
              <Col label="First Name" value={credentialingPrimaryContact.firstName} />
            )}
            {credentialingPrimaryContact?.lastName && (
              <Col label="Last Name" value={credentialingPrimaryContact.lastName} />
            )}
            {credentialingPrimaryContact?.title && (
              <Col label="Title" value={credentialingPrimaryContact.title} />
            )}
            {credentialingPrimaryContact?.phone && (
              <Col label="Phone" value={credentialingPrimaryContact.phone} tel />
            )}
            {credentialingPrimaryContact?.email && (
              <Col label="Email" value={credentialingPrimaryContact.email} email />
            )}
            {credentialingPrimaryContact?.fax && <Col label="Fax" value={credentialingPrimaryContact.fax} />}
            {credentialingPrimaryContact?.note && (
              <Col label="Note" value={credentialingPrimaryContact.note} />
            )}
          </Box>
        </Box>
      )}
      {hasFieldValue(credentialingSecondaryContact) && (
        <Box display="flex" flexDirection="column" px={4} pb={4} gap={2} rowGap={2}>
          <SectionTitle title="Credentialing Secondary Contact" />
          <Box display="flex" flexWrap="wrap" gap={3} rowGap={4}>
            {credentialingSecondaryContact?.firstName && (
              <Col label="First Name" value={credentialingSecondaryContact.firstName} />
            )}
            {credentialingSecondaryContact?.lastName && (
              <Col label="Last Name" value={credentialingSecondaryContact.lastName} />
            )}
            {credentialingSecondaryContact?.title && (
              <Col label="Title" value={credentialingSecondaryContact.title} />
            )}
            {credentialingSecondaryContact?.phone && (
              <Col label="Phone" value={credentialingSecondaryContact.phone} tel />
            )}
            {credentialingSecondaryContact?.email && (
              <Col label="Email" value={credentialingSecondaryContact.email} email />
            )}
            {credentialingSecondaryContact?.fax && (
              <Col label="Fax" value={credentialingSecondaryContact.fax} />
            )}
            {credentialingSecondaryContact?.note && (
              <Col label="Note" value={credentialingSecondaryContact.note} />
            )}
          </Box>
        </Box>
      )}
      {hasFieldValue(billingPrimaryContact) && (
        <Box display="flex" flexDirection="column" px={4} pb={4} gap={2} rowGap={2}>
          <SectionTitle title="Billing Primary Contact" />
          <Box display="flex" flexWrap="wrap" gap={3} rowGap={4}>
            {billingPrimaryContact?.firstName && (
              <Col label="First Name" value={billingPrimaryContact.firstName} />
            )}
            {billingPrimaryContact?.lastName && (
              <Col label="Last Name" value={billingPrimaryContact.lastName} />
            )}
            {billingPrimaryContact?.title && <Col label="Title" value={billingPrimaryContact.title} />}
            {billingPrimaryContact?.phone && <Col label="Phone" value={billingPrimaryContact.phone} tel />}
            {billingPrimaryContact?.email && <Col label="Email" value={billingPrimaryContact.email} email />}
            {billingPrimaryContact?.fax && <Col label="Fax" value={billingPrimaryContact.fax} />}
            {billingPrimaryContact?.note && <Col label="Note" value={billingPrimaryContact.note} />}
          </Box>
        </Box>
      )}
      {hasFieldValue(billingSecondaryContact) && (
        <Box display="flex" flexDirection="column" px={4} pb={4} gap={2} rowGap={2}>
          <SectionTitle title="Billing Secondary Contact" />
          <Box display="flex" flexWrap="wrap" gap={3} rowGap={4}>
            {billingSecondaryContact?.firstName && (
              <Col label="First Name" value={billingSecondaryContact.firstName} />
            )}
            {billingSecondaryContact?.lastName && (
              <Col label="Last Name" value={billingSecondaryContact.lastName} />
            )}
            {billingSecondaryContact?.title && <Col label="Title" value={billingSecondaryContact.title} />}
            {billingSecondaryContact?.phone && (
              <Col label="Phone" value={billingSecondaryContact.phone} tel />
            )}
            {billingSecondaryContact?.email && (
              <Col label="Email" value={billingSecondaryContact.email} email />
            )}
            {billingSecondaryContact?.fax && <Col label="Fax" value={billingSecondaryContact.fax} />}
            {billingSecondaryContact?.note && <Col label="Note" value={billingSecondaryContact.note} />}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Payer;
